import { cn } from "@/lib/utils";
import { ChevronDown } from "lucide-react";
import { useState } from "react";

interface DebugTraceEntry {
  function_name: string;
  trace_type?: string;
  summary_key?: string;
  cache_hit?: boolean;
  precompute_metadata?: Record<string, any>;
  precompute_payload?: Record<string, any>;
  fallback_used?: string;
  tool_params?: Record<string, any>;
  data_provenance?: Record<string, any>;
}

interface DebugTraceProps {
  traces: DebugTraceEntry[];
}

export function DebugTrace({ traces }: DebugTraceProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!traces || traces.length === 0) {
    return null;
  }

  return (
    <details className="mt-2 w-full">
      <summary
        className="cursor-pointer text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <ChevronDown
          className={cn(
            "h-3 w-3 transition-transform",
            isExpanded && "rotate-180"
          )}
        />
        Debug Trace ({traces.length} operations)
      </summary>
      <div className="mt-2 space-y-2 rounded border border-border/50 bg-muted/30 p-3 text-xs font-mono">
        {(() => {
          // Pair traces sequentially: each tool_call is followed by its cache trace.
          // Grouping by function_name alone collapses multiple calls to the same
          // function (e.g. 9x get_terminal_location_pax for 9 terminals) into one card.
          // Instead, pair by walking the trace list in order.
          type Group = {
            toolTrace?: DebugTraceEntry;
            cacheTrace?: DebugTraceEntry;
          };
          const groups: Group[] = [];

          traces.forEach((trace) => {
            if (trace.trace_type === "tool_call") {
              groups.push({ toolTrace: trace });
            } else if (trace.cache_hit !== undefined) {
              // Attach to the last unpaired group for this function_name
              const pending = [...groups]
                .reverse()
                .find(
                  (g) =>
                    g.toolTrace?.function_name === trace.function_name &&
                    !g.cacheTrace
                );
              if (pending) {
                pending.cacheTrace = trace;
              } else {
                // Cache-only trace (no preceding tool_call)
                groups.push({ cacheTrace: trace });
              }
            } else {
              groups.push({ cacheTrace: trace });
            }
          });

          return groups.map((group, idx) => {
            const toolTrace = group.toolTrace;
            const cacheTrace = group.cacheTrace;
            const funcName =
              toolTrace?.function_name ??
              cacheTrace?.function_name ??
              "unknown";

            return (
              <div
                key={idx}
                className="border-b border-border/30 pb-2 last:border-0 last:pb-0 space-y-1"
              >
                <div className="font-semibold text-foreground">{funcName}</div>

                {/* Tool invocation info */}
                {toolTrace && (
                  <>
                    {toolTrace.tool_params && (
                      <details className="mt-1 ml-2">
                        <summary className="cursor-pointer text-[10px] text-muted-foreground hover:text-foreground">
                          ▼ Params
                        </summary>
                        <pre className="mt-1 overflow-auto rounded bg-background p-2 text-[9px] border border-border/30">
                          {JSON.stringify(toolTrace.tool_params, null, 2)}
                        </pre>
                      </details>
                    )}
                    {toolTrace.data_provenance && (
                      <details className="mt-1 ml-2">
                        <summary className="cursor-pointer text-[10px] text-muted-foreground hover:text-foreground">
                          ▼ Provenance
                        </summary>
                        <pre className="mt-1 overflow-auto rounded bg-background p-2 text-[9px] border border-border/30">
                          {JSON.stringify(toolTrace.data_provenance, null, 2)}
                        </pre>
                      </details>
                    )}
                  </>
                )}

                {/* Cache lookup info */}
                {cacheTrace && (
                  <>
                    {cacheTrace.summary_key && (
                      <div className="text-muted-foreground ml-2">
                        Key:{" "}
                        <span className="text-foreground text-[10px]">
                          {cacheTrace.summary_key}
                        </span>
                      </div>
                    )}
                    {typeof cacheTrace.cache_hit === "boolean" && (
                      <div className="text-muted-foreground ml-2">
                        Cache:{" "}
                        <span
                          className={
                            cacheTrace.cache_hit
                              ? "text-green-600"
                              : "text-orange-600"
                          }
                        >
                          {cacheTrace.cache_hit ? "✓ HIT" : "✗ MISS"}
                        </span>
                      </div>
                    )}
                    {cacheTrace.precompute_metadata && (
                      <>
                        {cacheTrace.precompute_metadata.id && (
                          <div className="text-muted-foreground ml-2">
                            ID:{" "}
                            <span className="text-foreground text-[10px]">
                              {cacheTrace.precompute_metadata.id}
                            </span>
                          </div>
                        )}
                        {cacheTrace.precompute_metadata.target_date && (
                          <div className="text-muted-foreground ml-2">
                            Date:{" "}
                            <span className="text-foreground text-[10px]">
                              {cacheTrace.precompute_metadata.target_date}
                            </span>
                          </div>
                        )}
                        {cacheTrace.precompute_metadata.include_details !==
                          undefined && (
                          <div className="text-muted-foreground ml-2">
                            Detailed:{" "}
                            <span className="text-foreground text-[10px]">
                              {cacheTrace.precompute_metadata.include_details
                                ? "yes"
                                : "no"}
                            </span>
                          </div>
                        )}
                        {cacheTrace.precompute_metadata.updated_at && (
                          <div className="text-muted-foreground ml-2">
                            Updated:{" "}
                            <span className="text-foreground text-[10px]">
                              {new Date(
                                cacheTrace.precompute_metadata.updated_at
                              ).toLocaleString()}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                    {cacheTrace.fallback_used && (
                      <div className="text-muted-foreground ml-2">
                        Fallback:{" "}
                        <span className="text-foreground text-[10px]">
                          {cacheTrace.fallback_used}
                        </span>
                      </div>
                    )}
                    {cacheTrace.precompute_payload && (
                      <details className="mt-1 ml-2">
                        <summary className="cursor-pointer text-[10px] text-muted-foreground hover:text-foreground">
                          ▼ View full payload
                        </summary>
                        <pre className="mt-1 overflow-auto rounded bg-background p-2 text-[9px] border border-border/30 max-h-64">
                          {JSON.stringify(
                            cacheTrace.precompute_payload,
                            null,
                            2
                          )}
                        </pre>
                      </details>
                    )}
                  </>
                )}
              </div>
            );
          });
        })()}
      </div>
    </details>
  );
}
